/**
 * Convert a hex color string to its rgb channels. Accepts #rgb/#rrggbb and, dropping the
 * alpha channel, #rgba/#rrggbbaa shorthand and full forms
 * @param {string} hex hex color (with or without leading #)
 * @returns {{r: number, g: number, b: number}} rgb channels
 */
function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  const isShorthand = normalized.length === 3 || normalized.length === 4;
  const value = isShorthand
    ? normalized
        .substring(0, 3)
        .split('')
        .map((channel) => channel + channel)
        .join('')
    : normalized;
  return {
    r: parseInt(value.substring(0, 2), 16),
    g: parseInt(value.substring(2, 4), 16),
    b: parseInt(value.substring(4, 6), 16),
  };
}

/**
 * Get the WCAG relative luminance of an rgb color
 * @param {{r: number, g: number, b: number}} rgb rgb channels
 * @returns {number} relative luminance
 */
function getRelativeLuminance({ r, g, b }) {
  const [rs, gs, bs] = [r, g, b].map((channel) => {
    const sRGB = channel / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : ((sRGB + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Get the WCAG contrast ratio between two hex colors
 * @param {string} hexA first hex color
 * @param {string} hexB second hex color
 * @returns {number} contrast ratio, from 1 (no contrast) to 21 (max contrast)
 */
function getContrastRatio(hexA, hexB) {
  const luminanceA = getRelativeLuminance(hexToRgb(hexA));
  const luminanceB = getRelativeLuminance(hexToRgb(hexB));
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick the candidate hex color with the highest WCAG contrast ratio against a background color
 * @param {string} backgroundColor background hex color
 * @param {string[]} candidateColors candidate hex colors to choose from
 * @returns {string} the candidate color with the highest contrast against backgroundColor
 */
export function pickHighestContrastColor(backgroundColor, candidateColors) {
  const validCandidates = candidateColors.filter(Boolean);
  const contrastRatios = validCandidates.map((candidate) =>
    getContrastRatio(candidate, backgroundColor)
  );
  const highestContrastRatio = Math.max(...contrastRatios);
  return validCandidates[contrastRatios.indexOf(highestContrastRatio)];
}
